import { useEffect, useState } from "react";
import { Plus, Trash2, Clock, Paperclip, ImageIcon } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { api, fmtErr, fileUrl } from "../../lib/api";
import { INTERVENTION_TYPES, INTERVENTION_MAP } from "../../lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import ConfirmDelete from "../ConfirmDelete";

const inputCls =
  "w-full h-12 px-4 rounded-xl border border-zinc-200 bg-white text-base placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 transition-shadow";

const fmtDate = (d) => {
  try {
    return format(new Date(d), "d MMMM yyyy", { locale: es });
  } catch {
    return d;
  }
};

export default function HistoryTab({ machine, user }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    type: "mantenimiento",
    title: "",
    description: "",
  });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/machines/${machine.id}/interventions`).then(({ data }) => setItems(data));
  useEffect(() => { load(); }, [machine.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      await api.post(`/machines/${machine.id}/interventions`, fd);
      toast.success("Intervención registrada");
      setOpen(false);
      setForm({ date: new Date().toISOString().slice(0, 10), type: "mantenimiento", title: "", description: "" });
      setFiles([]);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    try {
      await api.delete(`/interventions/${item.id}`);
      toast.success("Intervención eliminada");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div data-testid="history-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl font-semibold tracking-tight">Historial</h2>
        <button
          onClick={() => setOpen(true)}
          data-testid="add-intervention-button"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 active:scale-95 transition-[background-color,transform] duration-200"
        >
          <Plus size={15} /> Añadir intervención
        </button>
      </div>

      {items === null ? (
        <div className="flex justify-center py-12">
          <div className="h-7 w-7 rounded-full border-2 border-zinc-300 border-t-zinc-900 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-2xl border border-dashed border-zinc-200" data-testid="history-empty">
          <Clock className="mx-auto text-zinc-300 mb-3" size={32} strokeWidth={1.5} />
          <p className="text-zinc-500 text-sm">Aún no hay intervenciones registradas.</p>
          <p className="text-zinc-400 text-sm mt-1">Registra mantenimientos, averías e inspecciones.</p>
        </div>
      ) : (
        <div className="relative pl-2">
          <div className="absolute left-[21px] top-2 bottom-2 w-px bg-zinc-200" />
          <div className="space-y-8">
            {items.map((item) => {
              const type = INTERVENTION_MAP[item.type] || INTERVENTION_MAP.nota;
              const Icon = type.icon;
              const canDelete = user?.role === "admin" || item.created_by_id === user?.id;
              return (
                <div key={item.id} className="relative flex gap-5 group" data-testid={`intervention-${item.id}`}>
                  <span className={`relative z-10 h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${type.classes}`}>
                    <Icon size={17} />
                  </span>
                  <div className="min-w-0 flex-1 pb-1">
                    <p className="text-xs text-zinc-400 capitalize">{fmtDate(item.date)}</p>
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-medium mt-1">{item.title}</h3>
                      {canDelete && (
                        <ConfirmDelete
                          testid={`intervention-delete-${item.id}`}
                          title="¿Eliminar esta intervención?"
                          description={`Se eliminará «${item.title}» del historial. Esta acción no se puede deshacer.`}
                          onConfirm={() => handleDelete(item)}
                          triggerClassName="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-full flex items-center justify-center text-zinc-300 hover:text-rose-600 hover:bg-rose-50 transition-all shrink-0"
                        />
                      )}
                    </div>
                    <span className={`inline-block mt-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${type.classes}`}>
                      {type.label}
                    </span>
                    {item.description && (
                      <p className="text-sm text-zinc-600 leading-relaxed mt-2.5 whitespace-pre-wrap">{item.description}</p>
                    )}
                    {item.attachments?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {item.attachments.map((att, i) =>
                          att.content_type?.startsWith("image/") ? (
                            <button key={i} onClick={() => window.open(fileUrl(att.path), "_blank")} data-testid={`intervention-attachment-${item.id}-${i}`}>
                              <img src={fileUrl(att.path)} alt={att.filename} className="h-16 w-16 rounded-xl object-cover border border-zinc-200" />
                            </button>
                          ) : (
                            <button
                              key={i}
                              onClick={() => window.open(fileUrl(att.path), "_blank")}
                              data-testid={`intervention-attachment-${item.id}-${i}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-100 text-xs text-zinc-600 hover:bg-zinc-200 transition-colors"
                            >
                              <Paperclip size={12} /> {att.filename}
                            </button>
                          )
                        )}
                      </div>
                    )}
                    <p className="text-xs text-zinc-400 mt-3">Registrado por {item.created_by}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl sm:max-w-md" data-testid="intervention-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Añadir intervención</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <input
                type="date"
                className={inputCls}
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                required
                data-testid="intervention-date-input"
              />
              <select
                className={inputCls}
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                data-testid="intervention-type-select"
              >
                {INTERVENTION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <input
              className={inputCls}
              placeholder="Título"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
              data-testid="intervention-title-input"
            />
            <textarea
              className="w-full min-h-[90px] px-4 py-3 rounded-xl border border-zinc-200 bg-white text-base placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 resize-none transition-shadow"
              placeholder="Descripción"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              data-testid="intervention-description-input"
            />
            <label className="flex flex-col items-center justify-center gap-2 h-24 rounded-2xl border border-dashed border-zinc-300 cursor-pointer hover:border-zinc-400 transition-colors text-sm text-zinc-500" data-testid="intervention-files-drop">
              <ImageIcon size={20} className="text-zinc-400" />
              {files.length > 0 ? `${files.length} foto(s) seleccionada(s)` : "Añadir fotos (opcional)"}
              <input
                type="file"
                multiple
                className="hidden"
                accept="image/*"
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                data-testid="intervention-files-input"
              />
            </label>
            <button
              type="submit"
              disabled={saving}
              data-testid="intervention-submit"
              className="w-full h-12 rounded-full bg-zinc-900 text-white font-medium hover:bg-zinc-800 active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-50"
            >
              {saving ? "Guardando…" : "Registrar intervención"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
